import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public one-click unsubscribe page. Verifies the signed token via GET
// /api/unsubscribe/{token} and shows a confirmation card. Idempotent — hitting
// the link a second time still succeeds with the same message.
export default function Unsubscribe() {
    const { token } = useParams();
    const [state, setState] = useState("loading"); // loading | success | error
    const [payload, setPayload] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const r = await axios.get(
                    `${API}/unsubscribe/${encodeURIComponent(token)}`,
                );
                if (cancel) return;
                setPayload(r.data);
                setState("success");
            } catch (err) {
                if (cancel) return;
                const detail = err.response?.data?.detail;
                setError(
                    typeof detail === "string"
                        ? detail
                        : "This unsubscribe link is invalid or expired.",
                );
                setState("error");
            }
        })();
        return () => {
            cancel = true;
        };
    }, [token]);

    return (
        <div
            data-testid="unsubscribe-page"
            className="min-h-screen flex items-center justify-center px-6 text-[color:var(--charcoal)]"
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                className="max-w-xl w-full border border-[color:var(--charcoal)] bg-[color:var(--cloud)] p-8 md:p-12"
            >
                <div className="font-mono font-semibold text-[12px] uppercase tracking-[0.28em] text-[color:var(--slate)] mb-4">
                    Stay Informed · Unsubscribe
                </div>

                {state === "loading" && (
                    <div className="flex items-center gap-3 font-body text-base">
                        <Loader2 size={20} className="animate-spin" />
                        Processing your request…
                    </div>
                )}

                {state === "success" && (
                    <>
                        <div className="flex items-start gap-3 mb-6">
                            <CheckCircle
                                size={28}
                                strokeWidth={1.5}
                                className="shrink-0 mt-1"
                            />
                            <h1 className="font-display text-2xl md:text-3xl leading-tight tracking-[-0.01em]">
                                You&rsquo;re unsubscribed.
                            </h1>
                        </div>
                        <p
                            data-testid="unsubscribe-message"
                            className="font-body text-base md:text-lg text-[color:var(--charcoal)]/90 leading-[1.6] mb-6"
                        >
                            {payload?.message ||
                                "You've been unsubscribed from Stay Informed dispatches."}
                            {payload?.email && (
                                <>
                                    {" "}We won&rsquo;t email{" "}
                                    <span className="font-mono font-semibold">
                                        {payload.email}
                                    </span>{" "}
                                    about new manifesto drops anymore.
                                </>
                            )}
                        </p>
                        <p className="font-body text-sm text-[color:var(--charcoal)]/70 mb-6">
                            Changed your mind? You can re-enable Stay Informed
                            anytime from your account page.
                        </p>
                        <div className="flex gap-3 flex-wrap">
                            <Link
                                to="/"
                                data-testid="unsubscribe-back-home"
                                className="pill-btn ghost"
                                style={{ padding: "0.6rem 1.1rem" }}
                            >
                                <ArrowLeft size={13} strokeWidth={1.6} />
                                Back to the manifesto
                            </Link>
                            <Link
                                to="/account"
                                data-testid="unsubscribe-manage"
                                className="pill-btn"
                                style={{ padding: "0.6rem 1.1rem" }}
                            >
                                Manage preferences
                            </Link>
                        </div>
                    </>
                )}

                {state === "error" && (
                    <>
                        <div className="flex items-start gap-3 mb-6">
                            <XCircle
                                size={28}
                                strokeWidth={1.5}
                                className="shrink-0 mt-1"
                            />
                            <h1 className="font-display text-2xl md:text-3xl leading-tight tracking-[-0.01em]">
                                We couldn&rsquo;t unsubscribe you.
                            </h1>
                        </div>
                        <p
                            data-testid="unsubscribe-error"
                            className="font-body text-base text-[color:var(--charcoal)]/90 leading-[1.6] mb-6"
                        >
                            {error}
                        </p>
                        <p className="font-body text-sm text-[color:var(--charcoal)]/70 mb-6">
                            You can still opt out from your account page —
                            sign in and toggle off Stay Informed under
                            preferences.
                        </p>
                        <div className="flex gap-3 flex-wrap">
                            <Link
                                to="/"
                                className="pill-btn ghost"
                                style={{ padding: "0.6rem 1.1rem" }}
                            >
                                <ArrowLeft size={13} strokeWidth={1.6} />
                                Back to the manifesto
                            </Link>
                            <Link
                                to="/account"
                                className="pill-btn"
                                style={{ padding: "0.6rem 1.1rem" }}
                            >
                                Sign in to manage
                            </Link>
                        </div>
                    </>
                )}
            </motion.div>
        </div>
    );
}
